import React, { useState, useEffect } from "react";
import { Box, Stack, Text } from "@mantine/core";

export interface ContentBlockTypeClockProps {
  /** Format for displaying time */
  timeFormat?: "12h" | "24h";

  /** Whether to show seconds */
  showSeconds?: boolean;

  /** Whether to show date */
  showDate?: boolean;

  /** Date format locale */
  locale?: string;

  /** Font style for clock */
  fontStyle?: "modern" | "digital" | "classic" | "outfit";

  /** Additional CSS class name for styling */
  className?: string;

  themeConfig?: any;

  /** Callback fired when time updates */
  onTimeUpdate?: ((date: Date) => void) | undefined;
}

export const ContentBlockTypeClock: React.FC<ContentBlockTypeClockProps> = ({
  timeFormat = "24h",
  showSeconds = true,
  showDate = true,
  locale = "en-US",
  //fontStyle = "outfit",
  className,
  themeConfig,
  onTimeUpdate,
}) => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  useEffect(() => {
    // Function to calculate milliseconds until next update
    const getMsUntilNextUpdate = (now: Date): number => {
      if (showSeconds) {
        // Update every second, synchronized to the clock
        return 1000 - now.getMilliseconds();
      } else {
        // Update every minute, synchronized to the clock
        const msUntilNextMinute =
          (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
        return msUntilNextMinute;
      }
    };

    // Update time and schedule next update
    const scheduleNextUpdate = () => {
      const now = new Date();
      setCurrentTime(now);
      onTimeUpdate?.(now);

      const msUntilNext = getMsUntilNextUpdate(now);
      return setTimeout(scheduleNextUpdate, msUntilNext);
    };

    // Initial schedule
    const timeoutId = scheduleNextUpdate();

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
    };
  }, [showSeconds, onTimeUpdate]);

  // Format time
  const formatTime = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      ...(showSeconds && { second: "2-digit" }),
      hour12: timeFormat === "12h",
    };

    return new Intl.DateTimeFormat(locale, options).format(date);
  };

  // Format date parts
  const formatDayOfWeek = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
    };
    return new Intl.DateTimeFormat(locale, options).format(date);
  };

  const formatMonthDay = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
    };
    return new Intl.DateTimeFormat(locale, options).format(date);
  };

  const formatYear = (date: Date): string => {
    return date.getFullYear().toString();
  };

  // // Get font family based on style
  // const getTimeFontFamily = (): string => {
  //   switch (fontStyle) {
  //     case "digital":
  //       return "'JetBrains Mono', 'Courier New', monospace";
  //     case "classic":
  //       return "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  //     case "outfit":
  //       return "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  //     case "modern":
  //     default:
  //       return "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  //   }
  // };

  // Get font weight based on style
  // const getTimeFontWeight = (): number => {
  //   switch (fontStyle) {
  //     case "digital":
  //       return 500;
  //     case "classic":
  //       return 400;
  //     case "outfit":
  //       return 500;
  //     case "modern":
  //     default:
  //       return 200;
  //   }
  // };

  const timeString = formatTime(currentTime);
  const dayOfWeek = formatDayOfWeek(currentTime);
  const monthDay = formatMonthDay(currentTime);
  const year = formatYear(currentTime);

  return (
    <Box
      className={`content-block-clock ${className || ""}`}
      h="100%"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--mantine-spacing-lg)",
        ...(themeConfig?.contentBlocks?.clock?.container && {
          ...themeConfig?.contentBlocks?.clock?.container,
        }),
      }}
    >
      <Stack align="center" gap="xs" className="content-block-clock__container">
        {showDate && (
          <Text
            className="content-block-clock__day-of-week"
            style={{
              textAlign: "center",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "var(--mantine-spacing-xs)",
              ...(themeConfig?.contentBlocks?.clock?.weekday && {
                ...themeConfig?.contentBlocks?.clock?.weekday,
              }),
            }}
          >
            {dayOfWeek}
          </Text>
        )}

        <Text
          className="content-block-clock__time"
          style={{
            ...(themeConfig?.contentBlocks?.clock?.time && {
              ...themeConfig?.contentBlocks?.clock?.time,
            }),
          }}
        >
          {timeString}
        </Text>

        {showDate && (
          <Stack
            align="center"
            gap={4}
            style={{ marginTop: "var(--mantine-spacing-xs)" }}
          >
            <Text
              className="content-block-clock__month-day"
              style={{
                textAlign: "center",
                letterSpacing: "0.01em",
                ...(themeConfig?.contentBlocks?.clock?.month && {
                  ...themeConfig?.contentBlocks?.clock?.month,
                }),
              }}
            >
              {monthDay}
            </Text>
            <Text
              className="content-block-clock__year"
              style={{
                textAlign: "center",
                letterSpacing: "0.05em",
                ...(themeConfig?.contentBlocks?.clock?.year && {
                  ...themeConfig?.contentBlocks?.clock?.year,
                }),
              }}
            >
              {year}
            </Text>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
